import api from "./api";
import type {
  AttendanceException,
  AttendanceRecord,
  AttendanceTeamSummary,
  AttendanceTodayResponse,
} from "@leave-system/shared";

export interface AttendanceListResponse<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
}

export interface AttendanceLocationInput {
  latitude: number;
  longitude: number;
  accuracy: number;
}

export interface AttendanceCheckInput {
  note: string;
  location: AttendanceLocationInput;
}

export interface AttendanceExceptionInput {
  attendanceRecordId?: string;
  type: "missing_checkout" | "manual_entry" | "time_correction";
  requestedCheckInAt?: string;
  requestedCheckOutAt?: string;
  reason: string;
}

export interface AttendanceExceptionResolutionInput {
  status: "resolved" | "rejected";
  resolutionNote?: string;
  checkInAt?: string;
  checkOutAt?: string;
}

export interface AttendanceRecordUpdateInput {
  checkInAt?: string;
  checkOutAt?: string;
  checkInNote?: string;
  checkOutNote?: string;
}

class AttendanceService {
  async getToday(): Promise<AttendanceTodayResponse> {
    const response = await api.get<{ data: AttendanceTodayResponse }>(
      "/api/v1/attendance/today"
    );
    return response.data.data;
  }

  async checkIn(input: AttendanceCheckInput): Promise<AttendanceRecord> {
    const response = await api.post<{ data: { record: AttendanceRecord } }>(
      "/api/v1/attendance/check-in",
      input
    );
    return response.data.data.record;
  }

  async checkOut(input: AttendanceCheckInput): Promise<AttendanceRecord> {
    const response = await api.post<{ data: { record: AttendanceRecord } }>(
      "/api/v1/attendance/check-out",
      input
    );
    return response.data.data.record;
  }

  async getHistory(page = 1, limit = 20): Promise<AttendanceListResponse<AttendanceRecord>> {
    const response = await api.get<{ data: AttendanceListResponse<AttendanceRecord> }>(
      `/api/v1/attendance/history?page=${page}&limit=${limit}`
    );
    return response.data.data;
  }

  async createException(
    input: AttendanceExceptionInput
  ): Promise<AttendanceException> {
    const response = await api.post<{ data: { exception: AttendanceException } }>(
      "/api/v1/attendance/exceptions",
      input
    );
    return response.data.data.exception;
  }

  async getExceptions(
    scope: "self" | "team" = "self",
    page = 1,
    limit = 20
  ): Promise<AttendanceListResponse<AttendanceException>> {
    const response = await api.get<{ data: AttendanceListResponse<AttendanceException> }>(
      `/api/v1/attendance/exceptions?scope=${scope}&page=${page}&limit=${limit}`
    );
    return response.data.data;
  }

  async getTeamSummary(
    page = 1,
    limit = 25,
    date?: string
  ): Promise<AttendanceListResponse<AttendanceTeamSummary>> {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });
    if (date) {
      params.append("date", date);
    }

    const response = await api.get<{ data: AttendanceListResponse<AttendanceTeamSummary> }>(
      `/api/v1/attendance/team?${params.toString()}`
    );
    return response.data.data;
  }

  async updateRecord(
    recordId: string,
    input: AttendanceRecordUpdateInput
  ): Promise<AttendanceRecord> {
    const response = await api.patch<{ data: { record: AttendanceRecord } }>(
      `/api/v1/attendance/records/${recordId}`,
      input
    );
    return response.data.data.record;
  }

  async resolveException(
    exceptionId: string,
    input: AttendanceExceptionResolutionInput
  ): Promise<AttendanceException> {
    const response = await api.post<{ data: { exception: AttendanceException } }>(
      `/api/v1/attendance/exceptions/${exceptionId}/resolve`,
      input
    );
    return response.data.data.exception;
  }
}

export const attendanceService = new AttendanceService();
export default attendanceService;
